import React, { useEffect, useRef } from 'react';
import { Checkbox, Col, Popover, Row, Space } from 'antd';
import { CustomButton, CustomTitle } from '@/components';
import { PencilIcon } from '@/assets/icons/icons';
import './RolesAndPermissions.css';

type Props = {
  disabled: boolean;
  open: boolean;
  selectedCount: number;
  totalCount: number;
  statuses: string[];
  value: string[];
  onChangeValue: (vals: string[]) => void;
  onEditClick: () => void;
  onClose: () => void;
  onReset: () => void;
  onApply: () => void;
  placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';
  selectAllByDefault?: boolean;
  title?: string;
};

const ShipmentStatusPopover: React.FC<Props> = ({
  disabled,
  open,
  selectedCount,
  totalCount,
  statuses,
  value,
  onChangeValue,
  onEditClick,
  onClose,
  onReset,
  onApply,
  placement = 'bottomLeft',
  selectAllByDefault = false,
  title = 'Viewable shipment statuses',
}) => {
  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    if (selectAllByDefault && (!Array.isArray(value) || value.length === 0)) {
      onChangeValue(statuses);
    }
  }, [selectAllByDefault, statuses, value, onChangeValue]);

  return (
    <Space size={6}>
      <span className='ft-count'>
        {selectedCount}/{totalCount}
      </span>

      <Popover
        open={!disabled && open}
        trigger='click'
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        placement={placement}
        getPopupContainer={() => document.body}
        overlayStyle={{ zIndex: 2000 }}
        overlayInnerStyle={{ borderRadius: 12, padding: 16, width: 300 }}
        content={
          <div className='ft-statusPopoverBody'>
            <CustomTitle
              text={title}
              fontWeight={600}
              fontSize={12}
              lineHeight='16px'
              color='var(--text-secondary)'
              marginBottom={12}
            />

            <Checkbox.Group
              className='ft-statusPopoverGroup'
              value={value}
              onChange={(vals) => onChangeValue(vals as string[])}
            >
              <Row gutter={[0, 8]}>
                {statuses.map((s) => (
                  <Col span={24} key={s}>
                    <Checkbox
                      className='custom-dropdown-export-coc-checkbox permission-font-12'
                      value={s}
                    >
                      <span className='checkbox-text'>{s}</span>
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>

            <div className='ft-statusPopoverActions'>
              <CustomButton
                key='reset'
                text='Reset'
                bgColor='transparent'
                color='var(--text-primary)'
                fontSize='12px'
                fontWeight='600'
                boxShadow='none'
                height='28px'
                width='auto'
                border='none'
                borderRadius='8px'
                onClick={onReset}
              />

              <CustomButton
                key='apply'
                text='Apply'
                bgColor='var(--text-primary)'
                color='var(--surface-container)'
                fontSize='12px'
                fontWeight='600'
                boxShadow='none'
                height='28px'
                width='auto'
                borderRadius='8px'
                onClick={onApply}
              />
            </div>
          </div>
        }
      >
        <div
          role='button'
          aria-label='edit-statuses'
          className={`ft-editCircle ${disabled ? 'isDisabled' : ''}`}
          aria-disabled={disabled}
          onClick={() => {
            if (!disabled) onEditClick();
          }}
        >
          <PencilIcon width={14} height={14} color='var(--text-primary)' />
        </div>
      </Popover>
    </Space>
  );
};

export default ShipmentStatusPopover;
